/* eslint-disable react/no-unescaped-entities */
import { GraduationCap, Globe2, Briefcase, Award } from 'lucide-react';
import { PageHero } from '../components/SectionHeading';
import { ScrollReveal, Stagger, StaggerItem } from '../components/Motion';
import { ASSETS } from '../data/content';
import { useLang } from '../i18n/LanguageContext';

const TRACK_EN = [
    { title: 'Azerbaijan — "Contract of the Century" (1994)', body: "Led Delta's participation as a founding member of AIOC alongside BP, Amoco, Unocal, and Pennzoil. ACG estimated recoverable reserves: 4.6 billion barrels." },
    { title: 'Centurion Energy (1999–2007)', body: "Directed Delta's 26% acquisition; appointed to Centurion's Board; oversaw growth to ~40,000 BOEPD. Divested to Dana Gas for C$1.25 billion — approximately 20x original investment." },
    { title: 'BTC Pipeline', body: "Oversaw Delta's participation in the early development of the Baku-Tbilisi-Ceyhan pipeline through Delta Hess." },
    { title: 'Algeria — Sonatrach Engagement (2023)', body: 'Personally led delegation to Algeria, meeting with the Minister of Energy and Mines.' },
];
const TRACK_AR = [
    { title: 'أذربيجان — "عقد القرن" (1994)', body: 'قاد مشاركة دلتا كعضو مؤسس في AIOC إلى جانب BP وأموكو ويونوكال وبنزويل. الاحتياطيات المقدرة القابلة للاستخراج من ACG: 4.6 مليار برميل.' },
    { title: 'سنتوريون للطاقة (1999–2007)', body: 'وجّه استحواذ دلتا بنسبة 26%؛ وعُيّن في مجلس إدارة سنتوريون؛ وأشرف على النمو إلى ~40,000 برميل مكافئ نفطي يوميًا. وتم البيع لدانة غاز بـ 1.25 مليار دولار كندي — نحو 20 ضعفًا من الاستثمار الأصلي.' },
    { title: 'خط أنابيب BTC', body: 'أشرف على مشاركة دلتا في التطوير المبكر لخط أنابيب باكو-تبليسي-جيهان عبر دلتا-هيس.' },
    { title: 'الجزائر — التعاون مع سوناطراك (2023)', body: 'قاد شخصيًا الوفد إلى الجزائر، والتقى بوزير الطاقة والمناجم.' },
];

const PRINCIPLES_EN = [
    { title: 'Long-term Thinking', body: 'Prioritizing sustainable value creation over short-term gains — from early Caspian positioning to today\'s frontier energy investments.' },
    { title: 'Relationship Capital', body: 'Building and maintaining deep, trust-based relationships with governments, national oil companies, and international partners across decades.' },
    { title: 'Decisive Agility', body: 'Combining the speed and flexibility of a privately held firm with the analytical discipline of an institutional investor.' },
    { title: 'Financial Creativity', body: 'Structuring innovative financing solutions — from project-based fund raising to balance sheet-supported bank finance — to unlock otherwise inaccessible opportunities.' },
    { title: 'Host-country Commitment', body: 'Ensuring that every Delta investment creates tangible, lasting economic and social value for the countries and communities in which the company operates.' },
];
const PRINCIPLES_AR = [
    { title: 'التفكير طويل الأمد', body: 'إعطاء الأولوية لخلق قيمة مستدامة على المكاسب قصيرة الأمد — من التموضع المبكر في بحر قزوين إلى استثمارات الطاقة في الأسواق الناشئة اليوم.' },
    { title: 'رأس مال العلاقات', body: 'بناء والحفاظ على علاقات عميقة قائمة على الثقة مع الحكومات وشركات النفط الوطنية والشركاء الدوليين على مدى عقود.' },
    { title: 'المرونة الحاسمة', body: 'الجمع بين سرعة ومرونة الشركة الخاصة وبين الانضباط التحليلي للمستثمر المؤسسي.' },
    { title: 'الإبداع المالي', body: 'هيكلة حلول تمويل مبتكرة — من جمع الأموال القائم على المشاريع إلى التمويل المصرفي المدعوم بالميزانية — لإطلاق فرص يصعب الوصول إليها بطرق أخرى.' },
    { title: 'الالتزام بالدول المضيفة', body: 'ضمان أن يخلق كل استثمار من دلتا قيمة اقتصادية واجتماعية ملموسة ومستدامة للبلدان والمجتمعات التي تعمل فيها الشركة.' },
];

const CHAIRMAN_BIO_EN = "Badr Bin Mohammed Al Aiban is the Founder and Chairman of Delta International Group Ltd. — the parent company of Delta International Energy. A graduate of the University of Southern California, Mr. Al-Aiban has dedicated more than 40 years to building one of Saudi Arabia's most distinguished privately held energy investment platforms.";
const CHAIRMAN_BIO_AR = 'بدر بن محمد العيبان هو مؤسس ورئيس مجلس إدارة مجموعة دلتا الدولية المحدودة — الشركة الأم لدلتا الدولية للطاقة. وهو خريج جامعة جنوب كاليفورنيا، وقد كرس السيد العيبان أكثر من 40 عامًا لبناء واحدة من أبرز منصات الاستثمار في الطاقة المملوكة للقطاع الخاص في المملكة العربية السعودية.';

const FOUNDING_VISION_BODY_EN = "Mr. Al-Aiban established Delta International Group in Jeddah in 1978 with a clear and enduring conviction: that a Saudi-rooted, privately held company could compete at the highest levels of the international oil and gas industry — combining the political intelligence and institutional relationships of the Gulf with the financial sophistication and technical rigor of the world's leading independent energy investors.";
const FOUNDING_VISION_BODY_AR = 'أسس السيد العيبان مجموعة دلتا الدولية في جدة عام 1978 بقناعة واضحة وراسخة: أن شركة سعودية الجذور مملوكة للقطاع الخاص يمكنها المنافسة على أعلى مستويات صناعة النفط والغاز الدولية — بالجمع بين الذكاء السياسي والعلاقات المؤسسية للخليج وبين التطور المالي والصرامة الفنية لأبرز المستثمرين المستقلين في الطاقة في العالم.';

const Leadership = () => {
    const { t, lang } = useLang();
    const TRACK = lang === 'ar' ? TRACK_AR : TRACK_EN;
    const PRINCIPLES = lang === 'ar' ? PRINCIPLES_AR : PRINCIPLES_EN;
    return (
        <div data-testid="leadership-page">
            <PageHero
                eyebrow={t('leadership.hero_eyebrow')}
                title={t('leadership.hero_title')}
                sub={t('leadership.hero_sub')}
                image={ASSETS.controlRoom}
            />

            {/* Chairman Profile */}
            <section className="bg-cream py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    <ScrollReveal className="lg:col-span-4">
                        <div className="aspect-[4/5] bg-navy-deep relative overflow-hidden">
                            <img
                                src={ASSETS.chairman}
                                alt="Badr Bin Mohammed Al Aiban — Founder, Chairman & CEO"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-navy-deep via-navy-deep/70 to-transparent pointer-events-none" />
                            <div className="absolute inset-x-0 bottom-0 p-7">
                                <div className="font-display text-white text-xl leading-tight">{t('leadership.chairman_name')}</div>
                                <div className="hairline-gold w-12 my-4" />
                                <div className="text-gold text-[10px] tracking-[0.25em] uppercase">{t('leadership.chairman_role')}</div>
                            </div>
                        </div>
                    </ScrollReveal>

                    <ScrollReveal delay={0.1} className="lg:col-span-8">
                        <div className="eyebrow mb-5">{t('leadership.profile_eyebrow')}</div>
                        <h2 className="editorial-h2 text-navy text-3xl md:text-5xl leading-tight">
                            {t('leadership.chairman_name')}
                        </h2>
                        <p className="mt-3 text-gold tracking-widest uppercase text-[11px]">
                            {t('leadership.chairman_title')}
                        </p>
                        <p className="mt-8 text-ink/80 text-base md:text-lg leading-relaxed">
                            {lang === 'ar' ? CHAIRMAN_BIO_AR : CHAIRMAN_BIO_EN}
                        </p>

                        <div className="mt-10 border-l-2 border-gold pl-6">
                            <div className="eyebrow mb-3">{t('leadership.founding_vision')}</div>
                            <p className="text-ink/80 leading-relaxed">
                                {lang === 'ar' ? FOUNDING_VISION_BODY_AR : FOUNDING_VISION_BODY_EN}
                            </p>
                        </div>

                        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-px bg-gold/20 border border-gold/20">
                            {[
                                { icon: GraduationCap, label: t('leadership.stat_education'), val: t('leadership.stat_education_val') },
                                { icon: Briefcase, label: t('leadership.stat_experience'), val: t('leadership.stat_experience_val') },
                                { icon: Globe2, label: t('leadership.stat_regions'), val: t('leadership.stat_regions_val') },
                                { icon: Award, label: t('leadership.stat_deals'), val: t('leadership.stat_deals_val') },
                            ].map((s) => (
                                <div key={s.label} className="bg-cream p-5">
                                    <s.icon className="w-5 h-5 text-gold mb-3" />
                                    <div className="text-[10px] tracking-widest uppercase text-ink/50">{s.label}</div>
                                    <div className="font-display text-navy text-xl mt-1">{s.val}</div>
                                </div>
                            ))}
                        </div>
                    </ScrollReveal>
                </div>
            </section>

            {/* Track Record */}
            <section className="bg-white py-24 md:py-32">
                <div className="max-w-6xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-5">{t('leadership.track_eyebrow')}</div>
                    <h2 className="editorial-h2 text-navy text-3xl md:text-4xl mb-12">
                        {t('leadership.track_h2')}
                    </h2>
                    <Stagger className="space-y-px bg-navy/10">
                        {TRACK.map((t, i) => (
                            <StaggerItem key={t.title} testid={`track-${i}`} className="bg-white grid grid-cols-1 md:grid-cols-12 gap-6 px-6 py-8 hover:bg-cream transition-colors">
                                <div className="md:col-span-1 metric-number text-2xl md:text-3xl text-gold">{String(i + 1).padStart(2, '0')}</div>
                                <div className="md:col-span-4 font-display text-navy text-lg md:text-xl leading-snug">
                                    {t.title}
                                </div>
                                <div className="md:col-span-7 text-ink/75 text-base leading-relaxed">
                                    {t.body}
                                </div>
                            </StaggerItem>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* Principles */}
            <section className="bg-navy-deep py-24 md:py-32 grain-overlay">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <div className="eyebrow mb-5">{t('leadership.principles_eyebrow')}</div>
                        <h2 className="editorial-h2 text-white text-3xl md:text-5xl">
                            {t('leadership.principles_h2')}
                        </h2>
                    </div>
                    <Stagger className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-px bg-gold/15">
                        {PRINCIPLES.map((p, i) => (
                            <StaggerItem key={p.title} testid={`principle-${i}`} className="bg-navy-deep p-8 hover:bg-navy-mid transition-colors">
                                <div className="metric-number text-2xl md:text-3xl mb-4">{String(i + 1).padStart(2, '0')}</div>
                                <h3 className="font-display text-white text-lg md:text-xl leading-snug mb-3">{p.title}</h3>
                                <p className="text-white/65 text-sm leading-relaxed">{p.body}</p>
                            </StaggerItem>
                        ))}
                    </Stagger>
                </div>
            </section>
        </div>
    );
};

export default Leadership;
